/**
 * Game session: owns the sim + attempt lifecycle for one level run.
 * The shell (main.ts) drives it; UI observes it. All gameplay state lives in
 * the sim — this class only orchestrates attempts, respawn timing, and stats.
 */
import { Sim, type SimState } from '../engine/sim';
import { RuntimeLevel } from '../engine/level/runtime';
import { ReplayRecorder, hashLevelText, type Replay } from '../engine/replay/replay';
import { stringifyLevel } from '../format/serialize';
import type { LevelData } from '../format/types';
import { getProgress, updateProgress, type LevelProgress } from './save';

export type SessionPhase = 'playing' | 'dead' | 'complete';

interface Checkpoint {
  state: SimState;
  rngState: number;
}

/** Delay between death and auto-respawn, seconds (shell-side, not sim). */
export const RESPAWN_DELAY_S = 0.7;

export class Session {
  readonly levelId: string;
  readonly level: RuntimeLevel;
  readonly sim: Sim;
  readonly recorder = new ReplayRecorder();
  phase: SessionPhase = 'playing';
  attempt = 1;
  /** Practice mode: player-placed checkpoints, separate from normal progress. */
  practiceMode = false;
  private checkpoints: Checkpoint[] = [];
  /** Platformer auto-checkpoint (from checkpoint objects) + death counter. */
  private autoCheckpoint: Checkpoint | null = null;
  private lastCheckpointId = -1;
  deaths = 0;

  get isPlatformer(): boolean {
    return this.level.mode === 'platformer';
  }
  /** Wall-clock seconds since death (for respawn delay). */
  deadForS = 0;
  /** Stats for the completion screen. */
  startedAtTick = 0;
  progressAtDeath = 0;
  private savedBest: number;

  /** Ephemeral sessions (editor playtests) never touch persistent progress. */
  readonly ephemeral: boolean;
  /** True when the just-finished completion was this level's first clear. */
  firstClear = false;
  /** Original level data (for replay finalization against its exact text). */
  private readonly levelData: LevelData;
  /** False once a run resumed from a checkpoint (replay no longer full-run). */
  private replayFromStart = true;
  private readonly usedStartPos: boolean;

  constructor(levelData: LevelData, opts: { useStartPos?: boolean; ephemeral?: boolean } = {}) {
    this.levelData = levelData;
    this.usedStartPos = opts.useStartPos ?? false;
    this.levelId = levelData.meta.name;
    this.level = new RuntimeLevel(levelData);
    this.sim = new Sim(this.level, 1, { useStartPos: opts.useStartPos ?? false });
    this.ephemeral = opts.ephemeral ?? false;
    if (this.ephemeral) {
      this.savedBest = 0;
      this.attempt = 1;
      return;
    }
    const p = getProgress(this.levelId);
    this.savedBest = p.best;
    this.attempt = p.attempts + 1;
    updateProgress(this.levelId, (prog) => {
      prog.attempts += 1;
    });
  }

  get best(): number {
    return Math.max(this.savedBest, this.sim.progress());
  }

  /** Step one sim tick with the given input word. */
  step(inputWord: number): void {
    if (this.phase !== 'playing') return;
    this.recorder.record(this.sim.state.tick, inputWord);
    this.sim.step(inputWord);
    // Platformer checkpoint objects auto-snapshot on first touch.
    if (this.isPlatformer && this.sim.state.lastCheckpointId !== this.lastCheckpointId) {
      this.lastCheckpointId = this.sim.state.lastCheckpointId;
      this.autoCheckpoint = { state: this.sim.snapshot(), rngState: this.sim.rng.state };
    }
    const status = this.sim.state.status;
    if (status === 'dead') {
      this.phase = 'dead';
      this.deadForS = 0;
      this.progressAtDeath = this.sim.progress();
      if (!this.practiceMode && !this.ephemeral) this.commitProgress(false);
    } else if (status === 'won') {
      this.phase = 'complete';
      if (!this.practiceMode && !this.ephemeral) this.commitProgress(true);
    }
  }

  /** Called on death after RESPAWN_DELAY_S, or on manual restart. */
  respawn(): void {
    if (this.sim.state.status === 'dead') this.deaths++;
    const cp = this.practiceMode
      ? this.checkpoints[this.checkpoints.length - 1]
      : this.isPlatformer
        ? (this.autoCheckpoint ?? undefined)
        : undefined;
    if (cp) {
      this.sim.restore(cp.state);
      this.sim.rng.state = cp.rngState;
      this.replayFromStart = false; // resumed mid-run: log no longer full-run
    } else {
      this.sim.reset();
      this.replayFromStart = true;
    }
    // Replays only make sense for full runs; practice invalidates the log.
    this.recorder.reset();
    this.attempt += 1;
    this.phase = 'playing';
    if (!this.practiceMode && !this.ephemeral) {
      updateProgress(this.levelId, (prog) => {
        prog.attempts += 1;
      });
    }
  }

  // --- Practice mode ---------------------------------------------------------

  setPractice(on: boolean): void {
    this.practiceMode = on;
    if (!on) this.checkpoints = [];
  }

  /** Place a checkpoint at the current sim state (only while alive). */
  placeCheckpoint(): boolean {
    if (!this.practiceMode || this.phase !== 'playing') return false;
    this.checkpoints.push({ state: this.sim.snapshot(), rngState: this.sim.rng.state });
    return true;
  }

  removeCheckpoint(): boolean {
    return this.checkpoints.pop() !== undefined;
  }

  get checkpointCount(): number {
    return this.checkpoints.length;
  }

  checkpointMarkers(): Array<{ x: number; y: number }> {
    return this.checkpoints.map((c) => ({ x: c.state.x, y: c.state.y }));
  }

  /** Restore-target sim time for music restarts after a respawn. */
  respawnTimeS(): number {
    const cp = this.practiceMode
      ? this.checkpoints[this.checkpoints.length - 1]
      : this.isPlatformer
        ? (this.autoCheckpoint ?? undefined)
        : undefined;
    return cp ? cp.state.tick / 240 : 0;
  }

  simTimeS(): number {
    return this.sim.state.tick / 240;
  }

  /** Full-run completion replay for publishing / score submission, or null
   * when the finished run would not re-simulate from tick 0 (practice,
   * checkpoint resume, startpos playtest, or not complete). */
  completionReplay(): { replay: Replay; levelText: string } | null {
    if (this.phase !== 'complete' || this.practiceMode || !this.replayFromStart) return null;
    if (this.sim.state.tick === 0) return null;
    if (this.usedStartPos && this.level.startPos) return null;
    const levelText = stringifyLevel(this.levelData);
    return {
      replay: this.recorder.finish(
        1,
        hashLevelText(levelText),
        this.sim.state.tick,
        this.sim.hash(),
      ),
      levelText,
    };
  }

  private commitProgress(completed: boolean): LevelProgress {
    const progress = this.sim.progress();
    const jumps = this.sim.state.jumps;
    if (completed) this.firstClear = getProgress(this.levelId).completions === 0;
    const result = updateProgress(this.levelId, (p) => {
      p.best = Math.max(p.best, completed ? 1 : progress);
      p.totalJumps += jumps;
      if (completed) p.completions += 1;
    });
    this.savedBest = result.best;
    return result;
  }
}
